/**
 * Inline MSP + user multi-select row shared by AI Thread Observer and Activity Report.
 * grep: observer scope filters, msp user multi select
 */

import React, { useMemo } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import LayeredMultiAutocompleteSelect from '../../components/LayeredMultiAutocompleteSelect';

export interface ObserverScopeMsp {
  id: number;
  name: string;
}

export interface ObserverScopeUser {
  id: string;
  email: string;
}

export interface ObserverScopeFiltersProps {
  msps: ObserverScopeMsp[];
  users: ObserverScopeUser[];
  selectedMspIds: string[];
  selectedUserIds: string[];
  onMspChange: (mspIds: string[]) => void;
  onUserChange: (userIds: string[]) => void;
  omitInternalUsers?: boolean;
  onOmitInternalUsersChange?: (omit: boolean) => void;
  omitAutoagent?: boolean;
  onOmitAutoagentChange?: (omit: boolean) => void;
  usersLoading?: boolean;
  usersDisabled?: boolean;
  mspLabel?: string;
  userLabel?: string;
  /** When true, only MSP multi-select is shown (Usage Quality always excludes internals). */
  hideUserFilter?: boolean;
}

const ObserverScopeFilters: React.FC<ObserverScopeFiltersProps> = ({
  msps,
  users,
  selectedMspIds,
  selectedUserIds,
  onMspChange,
  onUserChange,
  omitInternalUsers = false,
  onOmitInternalUsersChange,
  omitAutoagent = false,
  onOmitAutoagentChange,
  usersLoading = false,
  usersDisabled = false,
  mspLabel = 'MSP scope',
  userLabel = 'User scope',
  hideUserFilter = false,
}) => {
  const selectableMsps = useMemo(
    () => msps.filter((msp) => Number.isInteger(msp.id) && msp.id > 0),
    [msps],
  );

  const mspOptions = useMemo(
    () => selectableMsps.map((msp) => ({ value: String(msp.id), label: msp.name })),
    [selectableMsps],
  );

  const userOptions = useMemo(
    () => users.map((user) => ({ value: user.email, label: user.email })),
    [users],
  );

  return (
    <>
      <Box sx={{ flex: '1 1 180px', minWidth: 0, maxWidth: { xs: '100%', lg: '24%' } }}>
        <LayeredMultiAutocompleteSelect
          label={mspLabel}
          options={mspOptions}
          selectedValues={selectedMspIds}
          onChange={onMspChange}
          fullWidth
          size="small"
          listboxMaxHeight={520}
          allSelectedLabel="All MSPs"
          noneSelectedLabel="No MSPs selected"
          singularCountLabel="MSP"
          pluralCountLabel="MSPs"
        />
      </Box>
      {!hideUserFilter ? (
      <Box sx={{ flex: '1 1 180px', minWidth: 0, maxWidth: { xs: '100%', lg: '24%' } }}>
        <LayeredMultiAutocompleteSelect
          label={userLabel}
          options={userOptions}
          selectedValues={selectedUserIds}
          onChange={onUserChange}
          disabled={usersDisabled}
          loading={usersLoading}
          fullWidth
          size="small"
          listboxMaxHeight={520}
          allSelectedLabel="All users"
          noneSelectedLabel="No users selected"
          singularCountLabel="user"
          pluralCountLabel="users"
        />
        {onOmitInternalUsersChange || onOmitAutoagentChange ? (
          <Box
            sx={{
              mt: 0.25,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 1.5,
              rowGap: 0.25,
            }}
          >
            {onOmitInternalUsersChange ? (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={omitInternalUsers}
                    onChange={(event) => onOmitInternalUsersChange(event.target.checked)}
                    size="small"
                  />
                )}
                label="Omit internal users"
                sx={{
                  ml: 0,
                  mr: 0,
                  display: 'flex',
                  width: 'fit-content',
                  '& .MuiCheckbox-root': { p: 0.5, pl: 0 },
                  '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' },
                }}
              />
            ) : null}
            {onOmitAutoagentChange ? (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={omitAutoagent}
                    onChange={(event) => onOmitAutoagentChange(event.target.checked)}
                    size="small"
                  />
                )}
                label="Omit autoagent"
                sx={{
                  ml: 0,
                  mr: 0,
                  display: 'flex',
                  width: 'fit-content',
                  '& .MuiCheckbox-root': { p: 0.5, pl: 0 },
                  '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' },
                }}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
      ) : null}
    </>
  );
};

export default ObserverScopeFilters;
